import React, { useEffect, useState } from "react";
import {
  FaCloudUploadAlt,
  FaIdBadge,
  FaImage,
  FaSave,
  FaTimes,
} from "react-icons/fa";
import { toast } from "react-toastify";

import AdminDrawer from "./AdminDrawer";

import { uploadTeamImage } from "../../../../../services/teamServices";

import {
  buildTeamMemberPayload,
  emptyTeamMemberForm,
  mapTeamMemberToForm,
  resolveMediaUrl,
} from "../utils/teamManagerUtils";

export default function TeamMemberFormDrawer({
  isOpen,
  mode = "create",
  member,
  saving,
  onClose,
  onCreate,
  onUpdate,
}) {
  const isEdit = mode === "edit";

  const [formData, setFormData] = useState(emptyTeamMemberForm);
  const [photoFile, setPhotoFile] = useState(null);
  const [localPreview, setLocalPreview] = useState("");
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    if (isEdit && member) {
      setFormData(mapTeamMemberToForm(member));
    } else {
      setFormData(emptyTeamMemberForm);
    }

    setPhotoFile(null);
    setLocalPreview("");
    setUploading(false);
  }, [isOpen, isEdit, member]);

  useEffect(() => {
    if (!photoFile) return undefined;

    const url = URL.createObjectURL(photoFile);
    setLocalPreview(url);

    return () => URL.revokeObjectURL(url);
  }, [photoFile]);

  const title = isEdit ? "Modifier un responsable" : "Ajouter un responsable";

  const subtitle = isEdit
    ? "Mettez à jour l’identité, la fonction, la photo et l’ordre d’affichage."
    : "Ajoutez un élu, agent ou responsable à afficher sur la page contact.";

  const preview =
    localPreview || resolveMediaUrl(formData.photoURL, "/images/user_avatar.jpg");

  const handleChange = (event) => {
    const { name, value } = event.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const uploadPhotoIfNeeded = async () => {
    if (!photoFile) return formData.photoURL;

    const imageData = new FormData();
    imageData.append("image", photoFile);
    imageData.append("category", "team");

    try {
      setUploading(true);

      const response = await uploadTeamImage(imageData);

      return (
        response?.filePath ||
        response?.url ||
        response?.path ||
        response?.photoURL ||
        response?.data?.filePath ||
        response?.data?.url ||
        formData.photoURL
      );
    } catch (error) {
      console.error("Erreur upload photo responsable :", error);
      toast.error("Erreur lors de l'upload de la photo.");
      throw error;
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!formData.nom || !formData.prenom) {
      toast.error("Le nom et le prénom sont obligatoires.");
      return;
    }

    try {
      const finalPhotoUrl = await uploadPhotoIfNeeded();

      const payload = buildTeamMemberPayload({
        ...formData,
        photoURL: finalPhotoUrl,
      });

      if (isEdit) {
        await onUpdate?.(member.id, payload);
      } else {
        await onCreate?.(payload);
      }
    } catch {
      // Le toast d'erreur est déjà affiché côté upload.
    }
  };

  return (
    <AdminDrawer
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      subtitle={subtitle}
      width="max-w-4xl"
    >
      <form onSubmit={handleSubmit} className="flex min-h-full flex-col">
        <div className="flex-1 space-y-5 p-4 sm:p-6">
          <div className="rounded-[24px] border border-slate-200 bg-slate-50 p-4 dark:border-white/10 dark:bg-slate-900/60">
            <div className="mb-5 flex items-center gap-3">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                <FaIdBadge />
              </span>

              <div>
                <h3 className="font-bold text-slate-950 dark:text-white">
                  Identité du responsable
                </h3>

                <p className="text-sm text-slate-600 dark:text-slate-300">
                  Ces informations seront visibles sur la page publique de
                  contact.
                </p>
              </div>
            </div>

            <div className="grid gap-5 lg:grid-cols-[1fr_280px]">
              <div className="grid gap-4 sm:grid-cols-2">
                <InputField
                  label="Nom"
                  name="nom"
                  value={formData.nom}
                  onChange={handleChange}
                  required
                />

                <InputField
                  label="Prénom"
                  name="prenom"
                  value={formData.prenom}
                  onChange={handleChange}
                  required
                />

                <div className="sm:col-span-2">
                  <InputField
                    label="Fonction"
                    name="fonction"
                    value={formData.fonction}
                    onChange={handleChange}
                    placeholder="Ex : Responsable des sports"
                  />
                </div>

                <InputField
                  label="Position"
                  name="position"
                  type="number"
                  value={formData.position}
                  onChange={handleChange}
                />

                <div className="sm:col-span-2">
                  <InputField
                    label="URL de la photo"
                    name="photoURL"
                    value={formData.photoURL}
                    onChange={handleChange}
                    placeholder="https://... ou /uploads/..."
                  />
                </div>

                <div className="sm:col-span-2">
                  <label className="flex cursor-pointer items-center justify-center gap-2 rounded-2xl border border-dashed border-slate-300 bg-white px-4 py-4 text-sm font-bold text-slate-600 transition hover:border-primary hover:text-primary dark:border-white/10 dark:bg-slate-950 dark:text-slate-300">
                    <FaCloudUploadAlt />
                    {photoFile ? photoFile.name : "Choisir une photo"}
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(event) =>
                        setPhotoFile(event.target.files?.[0] || null)
                      }
                      className="hidden"
                    />
                  </label>
                </div>
              </div>

              <div className="rounded-[24px] border border-slate-200 bg-white p-4 dark:border-white/10 dark:bg-slate-950">
                <div className="mb-3 flex items-center gap-2">
                  <FaImage className="text-primary" />
                  <p className="text-sm font-bold text-slate-950 dark:text-white">
                    Aperçu photo
                  </p>
                </div>

                <div className="aspect-square overflow-hidden rounded-[24px] bg-slate-100 dark:bg-slate-900">
                  <img
                    src={preview}
                    alt="Aperçu responsable"
                    className="h-full w-full object-cover"
                    onError={(event) => {
                      event.currentTarget.src = "/images/user_avatar.jpg";
                    }}
                  />
                </div>

                <p className="mt-3 text-xs leading-5 text-slate-500 dark:text-slate-400">
                  Format conseillé : photo carrée ou portrait, bien cadrée sur
                  le visage.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 border-t border-slate-200 p-4 dark:border-white/10 sm:px-6">
          <button
            type="button"
            onClick={onClose}
            disabled={saving || uploading}
            className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700 transition hover:bg-slate-50 disabled:opacity-60 dark:border-white/10 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-white/10"
          >
            <FaTimes />
            Annuler
          </button>

          <button
            type="submit"
            disabled={saving || uploading}
            className="inline-flex items-center gap-2 rounded-2xl bg-primary px-4 py-2 text-sm font-bold text-white transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <FaSave />
            {saving || uploading
              ? "Enregistrement..."
              : isEdit
                ? "Mettre à jour"
                : "Créer"}
          </button>
        </div>
      </form>
    </AdminDrawer>
  );
}

function InputField({
  label,
  name,
  value,
  onChange,
  type = "text",
  required = false,
  placeholder = "",
}) {
  return (
    <div>
      <label className="mb-1 block text-sm font-bold text-slate-700 dark:text-slate-200">
        {label}
      </label>

      <input
        type={type}
        name={name}
        value={value ?? ""}
        onChange={onChange}
        required={required}
        placeholder={placeholder}
        className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:ring-2 focus:ring-primary/40 dark:border-white/10 dark:bg-slate-950 dark:text-white"
      />
    </div>
  );
}